import CustomButton from "@Common/CustomButton";
import { ManualReservationWrapper } from "./styles";
import { useTranslation } from "react-i18next";
import BookingInformationForm from "./bookingInformationForm";
import PricingInformationForm from "./pricingInformationForm";
import GustInformationForm from "./gustInformationForm";
import {
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import CustomLoading from "../../components/CustomLoading";
import dayjs from "dayjs";
import {
  APIS_KEYS,
  usePostManualReservation,
  useGetUserDetail,
  useTemplate,
} from "@services";
import { useNavigate } from "react-router-dom";
import { ToastMessageContext } from "@context/toast-message-cotnext";
import ReviewForm from "./reviewForm";
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6";
import { useSetRecoilState } from "recoil";
import { isManualReservationOpen } from "@store/index";
import { useQueryClient } from "@tanstack/react-query";
import { SaveButton } from "./bookingInformationForm/styles";
import useBreakPoinst from "@hooks/useBreakPoinst";
import {
  formatPhoneForBackend,
  resolveDirectPaymentAccepted,
} from "@utils";
export interface IFormManualReservation {
  booking_information: {
    propertyId: string;
    propertyName: string;
    checkIn: string | Date;
    checkOut: string | Date;
    isUserPaymentActive: boolean;
    nightPrice: number;
  };
  pricing_information: {
    nightPrice: number;
    totalPrice: number;
  };
  gust_information: {
    firstName: string;
    lastName: string;
    guestPhoneNumber: string;
    guestEmail: string;
    isAutoAccess: boolean;
  };
}
export default function ManualReservation() {
  const navigate = useNavigate();
  const isManualReservationOpenState = useSetRecoilState(
    isManualReservationOpen
  );
  const { isPhone } = useBreakPoinst();
  const { mutateAsync: mutatePostManualReservation } =
    usePostManualReservation();
  const { showSuccess } = useContext(ToastMessageContext);
  const { data: userData } = useGetUserDetail();
  // Direct payment sends the payment link + welcome book (a per-property
  // messaging template), so it is only available when the host has payments
  // enabled AND the selected property has a guest book. The toggle's per-property
  // default and disabled state live in BookingInformationForm; the submitted
  // value is derived the same way here so what the host sees can never diverge
  // from what we send.
  const { getTemplatesQuery } = useTemplate();
  const { data: templates } = getTemplatesQuery();

  const [isloaderState, setIsloaderState] = useState(false);

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [activeTab, setActiveTab] = useState<1 | 2 | 3 | 4>(1);
  const queryClient = useQueryClient();
  const tabs = useMemo(() => [
    {
      label: t("MANUALRESERVATION.RESERVATIONINFORMATION"),
      value: 1,
    },
    {
      label: t("MANUALRESERVATION.PRICINGDATA"),
      value: 2,
    },
    {
      label: t("MANUALRESERVATION.GUESTDATA"),
      value: 3,
    },
  ], [t]);

  const defaultValues = useMemo(() => ({
    booking_information: {
      propertyId: "",
      propertyName: "",
      checkIn: "",
      checkOut: "",
      isUserPaymentActive: false,
    },
    pricing_information: {
      nightPrice: 0,
      totalPrice: 0,
    },
    gust_information: {
      firstName: "",
      lastName: "",
      guestPhoneNumber: "",
      guestEmail: "",
      isAutoAccess: false,
    },
  }), []);

  const methods = useForm<IFormManualReservation>({
    mode: "all",
    defaultValues,
  });

  // One resolution for both the review popup's price quote and the submitted payload, so the
  // total the host confirms is the total the guest is charged (MS-618).
  const isDirectPaymentAccepted = useCallback(
    (booking: IFormManualReservation["booking_information"]) =>
      resolveDirectPaymentAccepted(
        (userData?.result as any)?.isUserPaymentActive,
        templates,
        Number(booking?.propertyId) || undefined,
        booking?.isUserPaymentActive
      ),
    [userData, templates]
  );

  const onSubmit = useCallback(async (value: IFormManualReservation) => {
    const { booking_information, pricing_information, gust_information } =
      value;

    if (
      !booking_information.propertyId ||
      !booking_information.checkIn ||
      !booking_information.checkOut
    ) {
      setActiveTab(1);
      return;
    }

    if (
      !pricing_information.nightPrice ||
      pricing_information.nightPrice <= 0 ||
      !pricing_information.totalPrice ||
      pricing_information.totalPrice <= 0
    ) {
      setActiveTab(2);
      return;
    }

    if (
      !gust_information.firstName ||
      !gust_information.lastName ||
      !gust_information.guestPhoneNumber ||
      !gust_information.guestEmail
    ) {
      setActiveTab(3);
      return;
    }
    if (activeTab !== 4) {
      setActiveTab(4);
      return;
    }
    setIsloaderState(true);
    const responseData = {
      id: 0,
      propertyId: Number(value?.booking_information?.propertyId),
      propertyName: value?.booking_information?.propertyName,
      checkIn: dayjs(value?.booking_information.checkIn).format("YYYY-MM-DD"),
      checkOut: dayjs(value?.booking_information.checkOut).format("YYYY-MM-DD"),
      totalPrice: Number(value?.pricing_information?.totalPrice),
      firstName: value?.gust_information?.firstName,
      lastName: value?.gust_information?.lastName,
      guestPhoneNumber: formatPhoneForBackend(
        value?.gust_information?.guestPhoneNumber,
        "e164"
      ),
      guestEmail: value?.gust_information?.guestEmail,
      isAutoAccess: false,
      HostNote: "",
      // Never claim direct payment the host can't actually use: the toggle is
      // rendered off (and disabled) when the SELECTED property has no guest book,
      // so keying this on that property stops us sending true on a booking that
      // would trip the backend's per-property guest-book check.
      isDirectPaymentAccepted: isDirectPaymentAccepted(
        value?.booking_information
      ),
    };
    const response = await mutatePostManualReservation({ ...responseData });

    if (response.status === 201 || response.status === 204) {
      setIsloaderState(false);
      navigate("/calendar");
      showSuccess(t("MANUALRESERVATION.RESERVATIONHASBEENSUCCESSFULLYSAVED"));
      queryClient.invalidateQueries({ queryKey: [APIS_KEYS.GETCALENDERDATA] });
      queryClient.invalidateQueries({
        queryKey: [APIS_KEYS.GETLISTINGMANAGEMENT],
      });
      isManualReservationOpenState(false);
    }
    setIsloaderState(false);
  }, [mutatePostManualReservation, navigate, showSuccess, t, queryClient, isManualReservationOpenState, activeTab, isDirectPaymentAccepted]);

  // Memoize tab click handler
  const handleTabClick = useCallback(async (tabValue: 1 | 2 | 3) => {
    const sectionName =
      activeTab === 1
        ? "booking_information"
        : activeTab === 2
          ? "pricing_information"
          : "gust_information";

    const isValid = await methods.trigger(sectionName as any);

    if (isValid) {
      setActiveTab(tabValue);
    }
  }, [activeTab, methods]);

  // Memoize navigation click handler
  const handleNavigationClick = useCallback(() => {
    if (activeTab === 4) {
      setActiveTab(1);
    } else {
      isManualReservationOpenState(false);
    }
  }, [activeTab, isManualReservationOpenState]);

  return (
    <ManualReservationWrapper isRTL={language} $isPhone={isPhone}>
      <div>
        {isloaderState && <CustomLoading />}
        <div className="containerHeader">
          <div>
            <div className="navigate">
              <button
                type="button"
                onClick={handleNavigationClick}
              >
                {language === "ar" ? <FaArrowLeftLong /> : <FaArrowRightLong />}
              </button>
            </div>
            <div className="containerInfo">
              <p>
                {t(
                  `MANUALRESERVATION.${activeTab === 4 ? "REVIEWTHEDATAANDCONFIRMTHERESERVATION" : "CREATEMANUALRESERVATION"}`
                )}
              </p>
            </div>
          </div>
        </div>
        {activeTab !== 4 && (
          <div className="containerTabs">
            {tabs.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`${item.value === activeTab ? "activeButton" : "normal"}`}
                onClick={() => handleTabClick(item.value as 1 | 2 | 3)}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
        <div>
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              id="formManualReservation"
            >
              {activeTab === 1 && <BookingInformationForm />}
              {activeTab === 2 && <PricingInformationForm />}
              {activeTab === 3 && <GustInformationForm />}
              {activeTab === 4 && (
                <ReviewForm
                  isDirectPaymentAccepted={isDirectPaymentAccepted(
                    methods.getValues("booking_information")
                  )}
                />
              )}
            </form>
          </FormProvider>

          {activeTab !== 4 ? (
            <SaveButton
              type="submit"
              form="formManualReservation"
              $isPhone={isPhone}
            >
              {t("MANUALRESERVATION.SAVE")}
            </SaveButton>
          ) : (
            <CustomButton
              label={t("MANUALRESERVATION.CONFIRMTHERESERVATION")}
              withBG
              style={{ width: "100%" }}
              customPadding="10px 27px"
              type="submit"
              form="formManualReservation"
            />
          )}
        </div>
      </div>
    </ManualReservationWrapper>
  );
}
